import { useCallback, useEffect, useRef, useState } from 'react';

import { KeyboardNavigationKey, ListValue } from '@xemplo/stateful-list-core';

import { UseDropdownProps } from './hooks.types';

type Modality = 'keyboard' | 'pointer';

export function useDropdownEvents(props: UseDropdownProps) {
  const {
    items,
    searchable,
    popoverRef,
    value,
    displayValue,
    onFocus,
    setDisplayValue,
    onListChange,
    onSearchChange,
    resolveLabel,
  } = props;

  // Temp states to restore label when closing without a new selection
  const [tempValue, setTempValue] = useState<ListValue>();
  const [tempLabel, setTempLabel] = useState<string>();

  /**
   * Set synchronously by `handleListChange` so the close it triggers can be told apart
   * from a close without a selection. State cannot do this — `setOpen` runs inside the
   * same event handler, so `tempValue` is still the previous render's value there.
   */
  const pendingSelectionRef = useRef(false);

  // Track last interaction modality globally (capture) so onFocus knows *how* we focused
  const lastModalityRef = useRef<Modality>('pointer');

  const getLabelFromValue = useCallback(
    (value: ListValue) => {
      // Prefer the cache (RHF path) so restore-on-close works even after server search has
      // replaced the list; fall back to the current items for the non-RHF path.
      return resolveLabel?.(value) || items.valueItemMap.get(value)?.label || '';
    },
    [items, resolveLabel],
  );

  /**
   * Blanks the field so a searchable dropdown can be typed into to filter. A
   * non-searchable one has nothing to type, so clearing it would only throw the floating
   * label away and back.
   */
  const clearSearchText = useCallback(() => {
    if (!searchable) return;
    setDisplayValue('');
    onSearchChange?.('');
  }, [searchable, setDisplayValue, onSearchChange]);

  /** Prevent typing when not searchable (but allow nav & submit keys) */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (searchable) {
        // When searchable, allow all typing but prevent Space from triggering list selection
        if (event.key === KeyboardNavigationKey.Space) {
          event.stopPropagation();
        }
        return;
      }

      switch (event.key) {
        case KeyboardNavigationKey.ArrowUp:
        case KeyboardNavigationKey.ArrowDown:
        case KeyboardNavigationKey.Tab:
        case KeyboardNavigationKey.Enter:
        case KeyboardNavigationKey.Space:
          return;
        default:
          event.preventDefault();
          event.stopPropagation();
          break;
      }
    },
    [searchable],
  );

  /** Toggle via container click */
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    const input = e.currentTarget.children.item(1) as HTMLInputElement | null;
    popoverRef.current?.setOpen((open) => {
      if (!input) return !open;
      open ? input.blur() : input.focus();
      return !open;
    });
  };

  /** Clicking the input also toggles */
  const handleInputClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    popoverRef.current?.setOpen(true);
  };

  /** Selection from the list */
  const handleListChange = (next?: ListValue) => {
    const label = next ? getLabelFromValue(next) : '';

    setTempValue(next);
    setTempLabel(label);

    // Restore the display text in the same update that closes the list. Leaving this to
    // a later path lets the input sit unfocused and blank for a frame, which drops the
    // floating label even though a value was only ever replaced by another value.
    pendingSelectionRef.current = true;
    setDisplayValue(label);

    onListChange?.(next);
    popoverRef.current?.setOpen(false);
  };

  /** Focus behavior based on modality */
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      clearSearchText();
      onFocus?.(e);

      // Only auto-open when focus came from keyboard navigation.
      if (lastModalityRef.current === 'keyboard') {
        popoverRef.current?.setOpen(true);
      }
    },
    [clearSearchText, onFocus, popoverRef],
  );

  /** Clear the search on open; restore the label when closing without a new selection */
  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      // Selecting an item no longer blurs the input, so reopening fires no focus event to
      // clear the search. Without this the list would reopen filtered by the label of the
      // item already chosen, leaving only that one option.
      if (open) {
        clearSearchText();
        return;
      }

      // A close triggered by picking an item has already had its text set, and `value`
      // here is still the pre-selection one — restoring from it would undo the choice.
      if (pendingSelectionRef.current) {
        pendingSelectionRef.current = false;
        return;
      }

      if (value == null || value === '') {
        setDisplayValue('');
        onSearchChange?.('');
        return;
      }

      if (tempValue === value) {
        setDisplayValue(tempLabel ?? '');
        onSearchChange?.(tempLabel ?? '');
      }
    },
    [clearSearchText, tempValue, tempLabel, value, setDisplayValue, onSearchChange],
  );

  /**
   * Effect responsible for tracking what's getting the focus into the component.
   * it can be either pointer (mouse/touch) or keyboard (tab/arrow keys).
   * Then the behavior of `handleFocus` can act accordingly.
   */
  useEffect(() => {
    const onPointerDown = () => {
      lastModalityRef.current = 'pointer';
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Only consider keys that typically move focus/open lists
      if (e.key === 'Tab' || e.key.startsWith('Arrow')) {
        lastModalityRef.current = 'keyboard';
      }
    };

    document.addEventListener('pointerdown', onPointerDown, { once: true });
    document.addEventListener('keydown', onKeyDown, { once: true });
  }, []);

  /**
   * Keeps tempValue and tempLabel in sync with the current value and available items.
   * Running on every value/items change (not just mount) ensures that:
   * - items loaded asynchronously after mount are reflected in close-without-select restore
   * - external value changes (e.g. RHF reset) update the restore target correctly
   * The display text is handled separately by useDropdownRhf via setSearchDisplayOnly.
   */
  useEffect(() => {
    if (!value) return;
    setTempValue(value);
    const label = getLabelFromValue(value);
    if (label) setTempLabel(label);
  }, [value, getLabelFromValue]);

  return {
    value,
    displayValue,
    handleContainerClick,
    handleInputClick,
    handleKeyDown,
    handleListChange,
    handleFocus,
    handleOnOpenChange,
  };
}
